/**
 * FeaturedAiTalentRibbon — Iter-295 — Responsive featured strip.
 *
 * Per the user's iter-295 spec, this component renders DIFFERENTLY on
 * desktop vs mobile/tablet:
 *
 *   • Desktop (≥md / 768px+): a single, premium agency-catalog hero
 *     card with the #1 weekly-ranked AI profile. Two-column layout
 *     (portrait | copy), gradient overlay, oversized name + city,
 *     rate range, and an "Open profile" CTA. Marcus H. (and friends)
 *     rotate weekly via the same deterministic ISO-week pick.
 *
 *   • Mobile + Tablet (<md): a horizontally-scrollable ribbon of 3–5
 *     AI talents with scroll-snap + a visible scroll indicator. The
 *     #1 pick stays the lead card on the left. Following cards are
 *     compact portrait tiles with name + rate. Does NOT block regular
 *     browsing — the rest of the AI grid renders directly below.
 *
 * Selection rules (deterministic per ISO week so it doesn't change
 * mid-session and every client sees the same Featured set):
 *   1. Filter to `talent_type='ai'` results with a real headshot.
 *   2. Rank by:
 *        is_top_profile     (Premium-grade hand-curated > everyone)
 *        is_premium
 *        avg_rating
 *        total_ratings
 *   3. Lead pick: from the top-3, pick the one whose ISO_week maps
 *      to (week % top3.length). Same week → same lead for every
 *      client. Following Monday a new lead rotates in.
 *   4. Ribbon secondary cards: the next up-to-4 profiles after the
 *      lead (so the ribbon size is between 3 and 5 cards).
 *
 * Hides itself if there are <2 eligible AI profiles.
 */
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Sparkles, Star, MapPin, ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { TalentCardMedia } from './discover/TalentCardMedia';

function isoWeek(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function rankEligibleAi(profiles) {
  return (profiles || [])
    .filter((t) => t.talent_type === 'ai' && t.profile_photo_url)
    .slice()
    .sort((a, b) => {
      const aw = (a.is_top_profile ? 4 : 0) + (a.is_premium ? 2 : 0)
                + (Number(a.avg_rating) || 0) / 5 + Math.log10((a.total_ratings || 0) + 1) / 10;
      const bw = (b.is_top_profile ? 4 : 0) + (b.is_premium ? 2 : 0)
                + (Number(b.avg_rating) || 0) / 5 + Math.log10((b.total_ratings || 0) + 1) / 10;
      return bw - aw;
    });
}

function pickLineup(profiles) {
  const ranked = rankEligibleAi(profiles);
  if (ranked.length === 0) return { lead: null, ribbon: [] };
  // Lead: deterministic rotation across this week's top-3.
  const top3 = ranked.slice(0, 3);
  const lead = top3[isoWeek() % top3.length];
  // Ribbon supporting cast: next 2-4 ranked profiles after the lead.
  // Total ribbon size is between 3 and 5 cards (1 lead + 2..4 followers).
  const followers = ranked.filter((p) => p.user_id !== lead.user_id).slice(0, 4);
  return { lead, ribbon: [lead, ...followers] };
}

function rateLabelFor(p) {
  return p.minimum_rate
    ? `Starting at $${Math.round(Number(p.minimum_rate))}/hr`
    : null;
}
function ratingLabelFor(p) {
  return p.avg_rating && Number(p.avg_rating) > 0
    ? Number(p.avg_rating).toFixed(1)
    : null;
}
function marketLabelFor(p) {
  if (!p.city) return null;
  return p.state ? `${p.city}, ${p.state}` : p.city;
}

/** ------- Desktop hero (unchanged premium agency-catalog look) ------- */
function HeroCard({ featured, onOpen }) {
  const rateLabel = rateLabelFor(featured);
  const ratingLabel = ratingLabelFor(featured);
  const market = marketLabelFor(featured);

  return (
    <div
      className="relative overflow-hidden rounded-2xl shadow-[0_8px_40px_rgba(124,58,237,0.18)] mb-5 sm:mb-6 border border-violet-200/50 bg-gradient-to-br from-violet-950 via-purple-900 to-fuchsia-900 text-white"
      data-testid="featured-ai-hero"
    >
      <div className="grid grid-cols-[minmax(180px,260px)_1fr] gap-1 items-stretch">
        <div className="relative">
          <TalentCardMedia
            photoUrl={featured.profile_photo_url}
            displayName={featured.display_name}
            aspect="portrait"
            testId={`featured-ai-hero-media-${featured.user_id}`}
          />
        </div>

        <div className="relative flex flex-col justify-center gap-2 px-6 py-6">
          <div className="absolute -top-6 -right-10 w-48 h-48 rounded-full bg-fuchsia-500/30 blur-3xl pointer-events-none" aria-hidden />
          <div className="absolute bottom-0 -left-12 w-40 h-40 rounded-full bg-violet-400/30 blur-3xl pointer-events-none" aria-hidden />

          <div className="relative flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-fuchsia-200">
            <Sparkles className="w-3.5 h-3.5" />
            Featured this week
          </div>

          <h3
            className="relative text-3xl font-bold leading-tight tracking-tight"
            data-testid="featured-ai-name"
          >
            {featured.display_name}
          </h3>

          <div className="relative flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-violet-100/90">
            {featured.role_name && <span>{featured.role_name}</span>}
            {market && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {market}
              </span>
            )}
            {ratingLabel && (
              <span className="flex items-center gap-1">
                <Star className="w-3.5 h-3.5 fill-amber-300 text-amber-300" />
                {ratingLabel}
              </span>
            )}
          </div>

          {rateLabel && (
            <p className="relative text-sm text-amber-200/90 font-medium">{rateLabel}</p>
          )}

          <div className="relative mt-2">
            <Button
              onClick={() => onOpen?.(featured)}
              className="bg-white text-violet-900 hover:bg-violet-50 font-semibold shadow-md"
              data-testid="featured-ai-open-btn"
            >
              Open profile
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** ---- Mobile/Tablet horizontal ribbon (scroll-snap + indicator) ---- */
function RibbonCard({ profile, isLead, onOpen }) {
  const rateLabel = rateLabelFor(profile);
  const ratingLabel = ratingLabelFor(profile);
  const market = marketLabelFor(profile);

  // Lead card slightly wider, supporting cards slim & uniform.
  const widthClass = isLead ? 'w-[78%] min-w-[78%]' : 'w-[46%] min-w-[46%]';

  return (
    <button
      type="button"
      onClick={() => onOpen?.(profile)}
      style={{ scrollSnapAlign: 'start' }}
      className={`${widthClass} relative shrink-0 overflow-hidden rounded-2xl border border-violet-200/40 bg-gradient-to-br from-violet-950 via-purple-900 to-fuchsia-900 text-left text-white shadow-[0_4px_20px_rgba(124,58,237,0.18)] transition-transform active:scale-[0.98]`}
      data-testid={`featured-ai-ribbon-card-${profile.user_id}`}
    >
      <TalentCardMedia
        photoUrl={profile.profile_photo_url}
        displayName={profile.display_name}
        aspect="portrait"
        testId={`featured-ai-ribbon-media-${profile.user_id}`}
      />

      {/* Eyebrow only on the lead card so it stays visually anchored. */}
      {isLead && (
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-white/95 text-violet-900 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow">
          <Sparkles className="w-3 h-3" />
          Featured
        </div>
      )}

      {/* Bottom info strip — sits on top of the gradient already
          baked into TalentCardMedia, so text remains legible. */}
      <div className="absolute inset-x-0 bottom-0 px-3 py-2.5">
        <h4
          className={`font-semibold leading-tight tracking-tight truncate ${
            isLead ? 'text-base' : 'text-sm'
          }`}
        >
          {profile.display_name}
        </h4>
        <div className="flex items-center gap-x-2 gap-y-0.5 flex-wrap text-[11px] text-violet-100/95">
          {market && (
            <span className="flex items-center gap-0.5 truncate min-w-0">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{market}</span>
            </span>
          )}
          {ratingLabel && (
            <span className="flex items-center gap-0.5">
              <Star className="w-3 h-3 fill-amber-300 text-amber-300" />
              {ratingLabel}
            </span>
          )}
        </div>
        {rateLabel && isLead && (
          <p className="text-[11px] text-amber-200/95 font-medium mt-0.5 truncate">
            {rateLabel}
          </p>
        )}
      </div>
    </button>
  );
}

function MobileRibbon({ ribbon, onOpen }) {
  const scrollerRef = useRef(null);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
      setAtEnd(remaining < 8);
    };
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [ribbon.length]);

  return (
    <div
      className="relative mb-5"
      data-testid="featured-ai-ribbon"
    >
      <div className="flex items-center gap-2 mb-2 px-0.5">
        <Sparkles className="w-3.5 h-3.5 text-violet-600" />
        <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-violet-700">
          Featured this week
        </span>
        <span className="ml-auto text-[11px] text-violet-700/70 flex items-center gap-0.5" aria-hidden>
          Swipe <ChevronRight className="w-3 h-3" />
        </span>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1"
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          scrollbarColor: '#a78bfa transparent',
        }}
        data-testid="featured-ai-ribbon-scroller"
      >
        {ribbon.map((p, idx) => (
          <RibbonCard
            key={p.user_id}
            profile={p}
            isLead={idx === 0}
            onOpen={onOpen}
          />
        ))}
      </div>

      {/* Right-edge fade affordance — hidden once the user has scrolled
          to the end, so the hint disappears after it's been understood. */}
      {!atEnd && ribbon.length > 1 && (
        <div
          className="pointer-events-none absolute top-7 right-0 bottom-3 w-10 bg-gradient-to-l from-white via-white/70 to-transparent"
          aria-hidden
        />
      )}
    </div>
  );
}

export default function FeaturedAiTalentRibbon({ talent, onOpen }) {
  const { lead, ribbon } = useMemo(() => pickLineup(talent), [talent]);

  if (!lead || (talent || []).length < 2) return null;

  return (
    <>
      {/* Mobile + tablet (<1024px): scrollable ribbon. */}
      <div className="lg:hidden">
        <MobileRibbon ribbon={ribbon} onOpen={onOpen} />
      </div>
      {/* Desktop (≥1024px): premium hero card. */}
      <div className="hidden lg:block">
        <HeroCard featured={lead} onOpen={onOpen} />
      </div>
    </>
  );
}
